import { useCallback, useEffect, useState } from "react"

const dateFmt = new Intl.DateTimeFormat("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" })
const shortFmt = new Intl.DateTimeFormat("pt-PT", { day: "2-digit", month: "2-digit" })
const timeFmt = new Intl.DateTimeFormat("pt-PT", { hour: "2-digit", minute: "2-digit" })
const monthFmt = new Intl.DateTimeFormat("pt-PT", { month: "short" })

export const fmtDate = (iso: string | null | undefined) => (iso ? dateFmt.format(new Date(iso)) : "—")
export const fmtShort = (iso: string | null | undefined) => (iso ? shortFmt.format(new Date(iso)) : "—")
export const fmtDateTime = (iso: string) => `${shortFmt.format(new Date(iso))} · ${timeFmt.format(new Date(iso))}`
/** `2026-03` → `mar`. */
export const fmtMonth = (ym: string) => monthFmt.format(new Date(`${ym}-01T12:00:00Z`)).replace(".", "")
export const fmtNum = (n: number | null | undefined, digits = 1) =>
  n == null ? "—" : n.toLocaleString("pt-PT", { minimumFractionDigits: digits, maximumFractionDigits: digits })
export const fmtSigned = (n: number | null | undefined) => (n == null ? "—" : `${n > 0 ? "+" : ""}${n}`)

/** `YYYY-MM-DD` (input date) ↔ ISO ao fim do dia de Lisboa (prazos). */
export const toDateInput = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : "")
export const fromDateInput = (v: string) => (v ? new Date(`${v}T18:00:00`).toISOString() : null)

export const initials = (name: string | null | undefined) =>
  (name ?? "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("")

/** Carrega dados com estado de loading/erro e `reload`. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fn, deps)
  useEffect(() => {
    let alive = true
    setLoading(true)
    run()
      .then((d) => {
        if (!alive) return
        setData(d)
        setError(null)
      })
      .catch((e: unknown) => {
        if (!alive) return
        const body = (e as { body?: { error?: string } })?.body
        setError(body?.error ?? (e instanceof Error ? e.message : String(e)))
      })
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [run, tick])
  return { data, error, loading, reload: () => setTick((t) => t + 1), setData }
}
